import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Grid, ListItemIcon, MenuItem } from '@mui/material'
import { AddRounded as AddRoundedIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import ItemList from '../../Common/ItemList/ItemList'
import AccountHeader from '../AccountHeader'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { type UserProductDesignStore } from '../../UserData/_stores/UserProductDesignStore'
import ProductSearchBar, { type ProductSearchParams } from './ProductSearchBar'
import { useSearchParams } from 'react-router'
import { FetchCollections } from '../../UserData/_actions/CollectionActions'
import SelectV2 from '../../Common/Form/SelectV2'
import NewCollectionDialog from '../Collections/NewCollectionDialog'
import CollectionSettingsButton from '../Collections/CollectionSettingsButton'
import usePrevious from '../../Common/_hooks/usePrevious'
import { type CollectionStore } from '../../UserData/_stores/CollectionStore'
import ProductDesignMainActions from './ProductDesignMainActions'
import ProductSkeletonList from './ProductSkeletonList'
import ProductDesignListActions from './ProductDesignListActions'
import { ClearProductDesignSelectedProduct, SetProductDesignSelectedProducts } from '../../UIData/_actions/Account/AccountStoreActions'
import useShiftRangeSelection from '../../Common/_hooks/useShiftRangeSelection'
import AccountFilterDrawer from '../Filters/AccountFilterDrawer'
import AccountFilterSection from '../Filters/AccountFilterSection'
import AccountLimitFilter from '../Filters/AccountLimitFilter'
import AccountLayoutFilter from '../Filters/AccountLayoutFilter'
import AccountSortFilter, { type AccountSortOption } from '../Filters/AccountSortFilter'
import { type ProductSortType } from '../../UserData/_stores/UserPrefsStore'
import { SaveUserPrefs } from '../../UserData/_actions/UserDataActions'
import AccountNoResults from '../AccountNoResults'

const SEARCH_FIELDS = ['search', 'categories', 'collections', 'sort']
const SORT_OPTIONS: ProductSortType[] = ['n-to-o', 'o-to-n', 'a-to-z', 'z-to-a']

export default function ProductDesignList() {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()

  const userLayout = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsLayout'))
  const userLimit = useAppSelector(state => state.get('userData').get('prefs').get('accountPageLimit'))
  const userSort = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsSort'))
  const collections = useAppSelector(state => state.get('userData').get('collections'))
  const productDesignSelectionClearCounter = useAppSelector(state => state.get('UIData').get('account').get('productDesignSelectionClearCounter'))

  // Init search bar with url search param
  const categories = searchParams.get('categories')
  const searchQuery = searchParams.get('search')
  const paramCollections = searchParams.get('collections')
  const sortParam = searchParams.get('sort')
  const sort = sortParam
    ? sortParam.split(',').filter((value): value is ProductSortType => SORT_OPTIONS.includes(value as ProductSortType))
    : []
  const [search, setSearch] = useState<ProductSearchParams>({
    categories: categories ? categories.split(',') : [],
    search: searchQuery ? searchQuery.split(',') : [],
    collections: paramCollections ? paramCollections.split(',') : [],
    sort: sort.length > 0 ? sort : [userSort],
  })
  const defaultSearch = useMemo<ProductSearchParams>(() => ({
    categories: [],
    search: [],
    collections: [],
    sort: [userSort],
  }), [userSort])

  const listId = useMemo(() => 'account-product-designs', [])
  const [selectedCollectionId, setSelectedCollectionId] = useState(Number(paramCollections) ?? 0)
  const [limit, setLimit] = useState(userLimit)
  const [layout, setLayout] = useState(userLayout)
  const selectionFilterKey = `${search.search.join(',')}|${search.categories.join(',')}|${search.collections.join(',')}`
  const visibleProductDesignItemIds = useAppSelector(state =>
    state.get('userData')
      .get('productDesigns')
      .get('itemLists')
      .get(listId)
      ?.get('items'))
  const visibleProductDesignIds = useMemo(() => (
    visibleProductDesignItemIds
      ?.valueSeq()
      .map(id => Number(id))
      .filter((id): id is number => Number.isFinite(id))
      .toArray() ?? []
  ), [visibleProductDesignItemIds])
  const { getSelectionIds, resetSelectionRange } = useShiftRangeSelection(visibleProductDesignIds)

  const [newCollectionDialogOpened, setNewCollectionDialogOpened] = useState(false)

  const selectedCollection = useMemo(() => {
    return collections.get(String(selectedCollectionId))
  }, [selectedCollectionId, collections])

  const onNewCollectionDialogOpen = useCallback(() => {
    setNewCollectionDialogOpened(true)
  }, [])

  const onNewCollectionDialogClose = useCallback(() => {
    setNewCollectionDialogOpened(false)
  }, [])

  const onCollectionAdd = useCallback((collectionId: number) => {
    setSelectedCollectionId(collectionId)
    setSearch({
      ...search,
      collections: [String(collectionId)]
    })
  }, [search])

  useEffect(() => {
    dispatch(FetchCollections())
  }, [])

  useEffect(() => {
    resetSelectionRange()
    dispatch(ClearProductDesignSelectedProduct())
  }, [dispatch, resetSelectionRange, selectionFilterKey])

  useEffect(() => {
    resetSelectionRange()
  }, [productDesignSelectionClearCounter, resetSelectionRange])

  useEffect(() => {
    return () => {
      dispatch(ClearProductDesignSelectedProduct())
    }
  }, [dispatch])

  const onSelectProductDesign = useCallback((id: number, shiftKey = false) => {
    dispatch(SetProductDesignSelectedProducts(getSelectionIds(id, shiftKey), true))
  }, [dispatch, getSelectionIds])

  const onUnselectProductDesign = useCallback((id: number, shiftKey = false) => {
    dispatch(SetProductDesignSelectedProducts(getSelectionIds(id, shiftKey), false))
  }, [dispatch, getSelectionIds])

  const renderItem = useCallback((item: UserProductDesignStore) => {
    return <ProductDesignMainActions
      item={item}
      view={layout}
      onSelect={onSelectProductDesign}
      onUnselect={onUnselectProductDesign}
      selectedCollection={selectedCollection}
    />
  }, [layout, onSelectProductDesign, onUnselectProductDesign, selectedCollection])

  const renderList = useCallback((items: JSX.Element | null, links: JSX.Element | null, loading: boolean) => {
    return <Box 
      component="div" 
      sx={{
        pt: layout === 'grid' ? 0 : {
          xs: 2,
          md: 3,
        }
      }}
    >
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {loading
          ? <ProductSkeletonList count={limit} layout={layout}/>
          : (items ?? <Grid item xs={12}>
            <AccountNoResults />
          </Grid>
          )
        }
      </Grid>

      {links}
    </Box>
  }, [t, limit, layout])

  const prevSelectedCollection = usePrevious(selectedCollection)

  useEffect(() => {
    // Reset collection if we lost the collection object (after deletion)
    if (selectedCollectionId > 0 && !selectedCollection && prevSelectedCollection?.get('id') === selectedCollectionId) {
      setSelectedCollectionId(0)
      setSearch(value => ({
        ...value,
        collections: []
      }))
    }
  }, [selectedCollectionId, selectedCollection])

  const renderCollectionItem = useCallback((collection: CollectionStore) => {
    return <MenuItem
      key={collection.get('id')}
      value={collection.get('id')}
    >{collection.get('name')}</MenuItem>
  }, [])

  const onChangeCollection = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === 'new') return

    setSelectedCollectionId(Number(e.target.value))
    setSearch(value => ({
      ...value,
      collections: Number(e.target.value) === 0 ? [] : [e.target.value]
    }))
  }, [])

  const onChangeSort = useCallback((nextSort: ProductSortType) => {
    setSearch(value => ({
      ...value,
      sort: [nextSort],
    }))

    if (nextSort === userSort) {
      return
    }

    const fd = new FormData()
    fd.append('accountProductsSort', nextSort)
    dispatch(SaveUserPrefs(fd, true))
  }, [dispatch, userSort])

  useEffect(() => {
    if (sortParam !== null) {
      return
    }

    setSearch(value => {
      if (value.sort[0] === userSort) {
        return value
      }

      return {
        ...value,
        sort: [userSort],
      }
    })
  }, [sortParam, userSort])

  const selectedSort = search.sort[0] ?? userSort
  const sortOptions: AccountSortOption<ProductSortType>[] = [
    { value: 'n-to-o', label: t('Newest first') },
    { value: 'o-to-n', label: t('Oldest first') },
    { value: 'a-to-z', label: t('A to Z') },
    { value: 'z-to-a', label: t('Z to A') },
  ]

  return <div>
    <AccountHeader
      title={selectedCollection ? selectedCollection.get('name') : t('Products')}
      titleActions={selectedCollection ? <CollectionSettingsButton
        collection={selectedCollection}
      /> : null}
      actions={<div style={{ minWidth: '100px' }}>
        <SelectV2
          value={selectedCollection ? selectedCollection.get('id') : 0}
          onChange={onChangeCollection}
        >
          <MenuItem value={0}>{t('All products')}</MenuItem>
          {collections.valueSeq().map(renderCollectionItem)}
          <MenuItem
            value="new"
            sx={{
              color: 'primary.main'
            }}
            onClick={onNewCollectionDialogOpen}
          >
            <ListItemIcon><AddRoundedIcon color="primary" /></ListItemIcon>
            {t('New collection')}
          </MenuItem>
        </SelectV2>
      </div>}
    />
    <ProductSearchBar
      placeholder={t('Search your products...')}
      value={search}
      onChange={setSearch}
    >
      <AccountFilterDrawer>
        <AccountFilterSection title={t('Display')}>
          <AccountSortFilter
            label={t('Sort by')}
            value={selectedSort}
            options={sortOptions}
            onChange={onChangeSort}
          />
          <AccountLimitFilter
            label={t('Results per page')}
            value={limit}
            onChange={setLimit}
          />
          <AccountLayoutFilter
            label={t('Layout')}
            value={layout}
            onChange={setLayout}
          />
        </AccountFilterSection>
      </AccountFilterDrawer>
    </ProductSearchBar>
    <ProductDesignListActions
      listId={listId}
      search={search}
      selectedCollection={selectedCollection}
    />
    <ItemList
      listId={listId}
      dataType={'productDesigns'}
      item={renderItem}
      searchQuery={search}
      defaultSearchQuery={defaultSearch}
      searchFields={SEARCH_FIELDS}
      limit={limit}
      render={renderList}
    />

    <NewCollectionDialog
      opened={newCollectionDialogOpened}
      onClose={onNewCollectionDialogClose}
      onAdd={onCollectionAdd}
    />
  </div>
}
